import { makeObservable, observable, runInAction } from 'mobx'
import type { StaticModelCollection } from '../firestore-mobx/model'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import { UserPromotion } from '../models/UserPromotion'
import {
  createUserPromotion,
  deleteUserPromotion,
  getUserPromotions,
  updateUserPromotion,
} from '../firestore/UserPromotion'
import { getUserPromotionRedemptionsForUserPromotion } from '../firestore/UserPromotionRedemption'
import type { UserPromotionRedemption } from '../models/UserPromotionRedemption'
import { Promotion } from '../models/Promotion'
import { getPromotions } from '../firestore/Promotion'

export class AdminUserPromotionCubit extends Cubit {
  repository: FirebaseRepository

  userId: string
  userPromotions: StaticModelCollection<UserPromotion>
  userPromotionRedemptions = observable.map<string, UserPromotionRedemption[]>()
  promotions: StaticModelCollection<Promotion>

  slideDecksInitialized = false

  constructor(repository: FirebaseRepository, userId: string) {
    super()
    this.userId = userId
    this.repository = repository

    this.userPromotions = UserPromotion.emptyCollection(repository)
    this.promotions = Promotion.emptyCollection(repository)

    makeObservable(this)
  }

  initialize(): void {
    this.startUserPromotionStream()

    this.addStream(getPromotions(this.repository), (promotions) => {
      runInAction(() => {
        this.promotions.replaceModels(promotions)
      })
    })
  }

  startUserPromotionStream(): void {
    this.addStream(
      getUserPromotions(this.repository, {
        userId: this.userId,
      }),
      (promotions) => {
        this.userPromotions.replaceModels(promotions)
        this.startRedemptionStreams(promotions)
      }
    )
  }

  startRedemptionStreams(promotions: UserPromotion[]): void {
    for (const promotion of promotions) {
      this.addStream(
        getUserPromotionRedemptionsForUserPromotion(this.repository, {
          userId: this.userId,
          userPromotionId: promotion.id,
        }),
        (redemptions) => {
          this.userPromotionRedemptions.set(promotion.id, redemptions)
        }
      )
    }
  }

  updateUserPromotion(promotionId: string, data: UserPromotion['data']) {
    return updateUserPromotion(this.repository, this.userId, promotionId, data)
  }

  createUserPromotion(data: UserPromotion['data']) {
    return createUserPromotion(this.repository, this.userId, data)
  }

  deleteUserPromotion(promotionId: string) {
    return deleteUserPromotion(this.repository, this.userId, promotionId)
  }
}
